// src/redux/settingsPages/userData/userDataSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchUserDataSettings,
  updateUserDataSettings,
  resetUserDataSettings,
  requestDataExport,
  fetchDataUsage,
  deleteAccountThunk,
} from './userDataActions';

// Frontend-safe defaults that match your AccountDataPage toggles
const DEFAULTS = {
  marketData: true,
  personalizedRecommendations: true,
  analyticsTracking: false,
  thirdPartySharing: false,
  emailNotifications: true,
  pushNotifications: true,
  smsNotifications: false,
};

const initialState = {
  settings: { ...DEFAULTS },
  loading: false,
  error: null,

  saving: false,
  saveError: null,

  usage: null,
  usageLoading: false,
  usageError: null,

  export: { status: 'idle', jobId: null, error: null }, // idle | loading | queued | done | failed
  deleteStatus: 'idle', // idle | loading | scheduled | failed
  deleteError: null,
};

const slice = createSlice({
  name: 'userData',
  initialState,
  reducers: {
    clearUserDataError(state) {
      state.error = null;
      state.saveError = null;
      state.usageError = null;
      state.export.error = null;
      state.deleteError = null;
    },
    // If you want a quick local toggle before saving:
    toggleSettingLocal(state, action) {
      const key = action.payload;
      if (Object.prototype.hasOwnProperty.call(state.settings, key)) {
        state.settings[key] = !state.settings[key];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // load
      .addCase(fetchUserDataSettings.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchUserDataSettings.fulfilled, (s, a) => {
        s.loading = false;
        // Merge backend settings over defaults to keep missing keys safe
        s.settings = { ...DEFAULTS, ...(a.payload || {}) };
      })
      .addCase(fetchUserDataSettings.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload || 'Failed to load settings';
        // keep defaults so UI remains usable
        s.settings = { ...DEFAULTS };
      })

      // save/update
      .addCase(updateUserDataSettings.pending, (s) => { s.saving = true; s.saveError = null; })
      .addCase(updateUserDataSettings.fulfilled, (s, a) => {
        s.saving = false;
        s.settings = { ...s.settings, ...(a.payload || {}) };
      })
      .addCase(updateUserDataSettings.rejected, (s, a) => {
        s.saving = false;
        s.saveError = a.payload || 'Failed to save settings';
      })

      // reset defaults
      .addCase(resetUserDataSettings.pending, (s) => { s.saving = true; s.saveError = null; })
      .addCase(resetUserDataSettings.fulfilled, (s, a) => {
        s.saving = false;
        s.settings = { ...DEFAULTS, ...(a.payload || {}) };
      })
      .addCase(resetUserDataSettings.rejected, (s, a) => {
        s.saving = false;
        s.saveError = a.payload || 'Failed to reset settings';
      })

      // usage
      .addCase(fetchDataUsage.pending, (s) => { s.usageLoading = true; s.usageError = null; })
      .addCase(fetchDataUsage.fulfilled, (s, a) => {
        s.usageLoading = false;
        s.usage = a.payload || {};
      })
      .addCase(fetchDataUsage.rejected, (s, a) => {
        s.usageLoading = false;
        s.usageError = a.payload || 'Failed to load data usage';
      })

      // export
      .addCase(requestDataExport.pending, (s) => { s.export.status = 'loading'; s.export.error = null; s.export.jobId = null; })
      .addCase(requestDataExport.fulfilled, (s, a) => {
        s.export.status = a.payload?.status || 'queued';
        s.export.jobId = a.payload?.job_id ?? null;
      })
      .addCase(requestDataExport.rejected, (s, a) => {
        s.export.status = 'failed';
        s.export.error = a.payload || 'Export request failed';
      })

      // delete account
      .addCase(deleteAccountThunk.pending, (s) => { s.deleteStatus = 'loading'; s.deleteError = null; })
      .addCase(deleteAccountThunk.fulfilled, (s, a) => {
        // Expect { scheduled: true } or similar
        s.deleteStatus = (a.payload?.scheduled || a.payload?.status === 'scheduled') ? 'scheduled' : 'scheduled';
      })
      .addCase(deleteAccountThunk.rejected, (s, a) => {
        s.deleteStatus = 'failed';
        s.deleteError = a.payload || 'Account deletion failed';
      });
  },
});

export const { clearUserDataError, toggleSettingLocal } = slice.actions;
export default slice.reducer;
